import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, BellOff, RefreshCw, Bug } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useBadgeAPI } from '@/hooks/useBadgeAPI';
import { IosInstallHint } from '@/components/IosInstallHint';

export const PushNotificationSettings = () => {
  const {
    isSupported,
    isSubscribed,
    loading,
    subscribeToPush,
    unsubscribeFromPush,
    syncSubscription,
    getDiagnosticInfo,
    iosNeedsInstall,
  } = usePushNotifications();
  const { isSupported: badgeSupported, currentBadgeCount, clearBadge } = useBadgeAPI();
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncSubscription();
    } finally {
      setSyncing(false);
    }
  };

  const handleShowDiagnostics = async () => {
    if (!showDiagnostics) {
      const info = await getDiagnosticInfo();
      setDiagnosticInfo(info);
    }
    setShowDiagnostics(!showDiagnostics);
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Push ծանուցումներ
          </CardTitle>
          <CardDescription>
            Ձեր browser-ը չի աջակցում push ծանուցումները
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push ծանուցումներ
        </CardTitle>
        <CardDescription>
          Ստանալ native ծանուցումներ բրաուզերի միջոցով
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <IosInstallHint />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">
                {isSubscribed ? 'Ակտիվ' : 'Ոչ ակտիվ'}
              </p>
              {badgeSupported && (
                <Badge variant="outline" className="text-xs">
                  Badge: {currentBadgeCount}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Push ծանուցումների ընթացիկ կարգավիճակ
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {badgeSupported && currentBadgeCount > 0 && (
              <Button
                onClick={clearBadge}
                variant="outline"
                size="sm"
                className="w-full justify-center sm:w-auto"
              >
                Մաքրել Badge
              </Button>
            )}
            <Button
              onClick={handleSync}
              disabled={syncing}
              variant="outline"
              size="sm"
              className="w-full justify-center sm:w-auto"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Վերականգնում...' : 'Վերաստուգել'}
            </Button>
            <Button
              onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
              disabled={loading || iosNeedsInstall}
              variant={isSubscribed ? "destructive" : "default"}
              className="w-full justify-center sm:w-auto"
            >
              {loading ? "Սպասեք..." : isSubscribed ? "Անջատել" : "Ակտիվացնել"}
            </Button>

          </div>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Push ծանուցումները թույլ են տալիս ստանալ native ծանուցումներ նույնիսկ երբ հավելվածը բաց չէ։ 
            Դուք կստանաք ծանուցումներ նոր դասեր, հաղորդագրություններ և այլ կարևոր իրադարձությունների մասին։
          </p>
          
          {/* Browser permission status indicator */}
          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm font-medium mb-2">Browser ծանուցումների կարգավիճակ:</p>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                typeof window !== 'undefined' && 'Notification' in window 
                  ? Notification.permission === 'granted' 
                    ? 'bg-green-500' 
                    : Notification.permission === 'denied' 
                      ? 'bg-red-500' 
                      : 'bg-amber-500'
                  : 'bg-muted-foreground'
              }`}></div>
              <span className="text-sm">
                {typeof window !== 'undefined' && 'Notification' in window 
                  ? Notification.permission === 'granted' 
                    ? 'Թույլատրված' 
                    : Notification.permission === 'denied' 
                      ? 'Մերժված (Browser-ի կարգավորումներից ակտիվացրեք)' 
                      : 'Չի հարցվել'
                  : 'Չի աջակցվում'
                }
              </span>
            </div>
            
            {typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'denied' && (
              <div className="mt-2 bg-amber-500/15 border border-amber-500/40 rounded text-xs text-amber-600 dark:text-amber-400 p-2">
                <strong>Որպեսզի ակտիվացնեք ծանուցումները:</strong><br/>
                1. Սեղմեք browser-ի address bar-ի կողքի 🔒 icon-ի վրա<br/>
                2. Գտեք "Ծանուցումներ" բաժինը<br/>
                3. Փոխեք "Մերժել"-ից "Թույլատրել"<br/>
                4. Վերբեռնեք էջը և փորձեք կրկին
              </div>
            )}
          </div>
          
          {badgeSupported && (
            <div className="p-3 bg-green-500/10 border border-border/70 rounded-md">
              <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">
                ✓ Badge API ակտիվ է
              </p>
              <p className="text-sm text-muted-foreground">
                App icon-ի վրա կցուցադրվեն չկարդացած ծանուցումների քանակը
              </p>
            </div>
          )}
          {!badgeSupported && (
            <div className="p-3 bg-amber-500/15 border border-amber-500/40 rounded-md">
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
                📱 Badge-ները մոբայլ սարքերի համար
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                Չկարդացած ծանուցումների քանակը app icon-ի վրա ցուցադրելու համար անհրաժեշտ է PWA տեղադրում:
              </p>
              <div className="text-xs text-muted-foreground space-y-1">
                <p><strong>Android Chrome:</strong></p>
                <p>• Բացեք menu (⋮) → "Add to Home screen"</p>
                <p><strong>iOS Safari:</strong></p>
                <p>• Սեղմեք Share (📤) → "Add to Home Screen"</p>
                <p><strong>Desktop:</strong></p>
                <p>• Address bar-ում կհայտնվի install (⬇) կոճակ</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Diagnostic Section */}
        <div className="pt-4 border-t">
          <Button
            onClick={handleShowDiagnostics}
            variant="ghost"
            size="sm"
            className="w-full"
          >
            <Bug className="h-4 w-4 mr-2" />
            {showDiagnostics ? 'Փակել' : 'Ցուցադրել'} Տեխնիկական տեղեկություններ
          </Button>
          
          {showDiagnostics && diagnosticInfo && (
            <div className="mt-3 p-3 bg-muted rounded-md">
              <p className="text-sm font-medium mb-2">Տեխնիկական ստուգում:</p>
              <div className="text-xs space-y-1 font-mono">
                <div>Browser push: {diagnosticInfo.browserSubscription ? '✅' : '❌'}</div>
                <div>Database: {diagnosticInfo.dbSubscription ? '✅' : '❌'}</div>
                <div>Permission: {diagnosticInfo.notificationPermission}</div>
                <div>Service Worker: {diagnosticInfo.serviceWorkerReady ? '✅' : '❌'}</div>
                <div>State: {diagnosticInfo.isSubscribedState ? 'Subscribed' : 'Not subscribed'}</div>
                <div>Backup: {diagnosticInfo.hasLocalStorageBackup ? '✅' : '❌'}</div>
                {diagnosticInfo.browserEndpoint && (
                  <div>Endpoint: {diagnosticInfo.browserEndpoint}</div>
                )}
                {diagnosticInfo.dbUpdatedAt && (
                  <div>DB Updated: {new Date(diagnosticInfo.dbUpdatedAt).toLocaleString()}</div>
                )}
                {diagnosticInfo.error && (
                  <div className="text-red-600">Error: {diagnosticInfo.error}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};